import { Facebook, Linkedin, Instagram, Youtube, Send, Building2, Mail, Phone, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const footerLinks = {
  markets: [
    { name: 'Forex', href: '/markets/forex' },
    { name: 'Commodities', href: '/markets/commodities' },
    { name: 'Indices', href: '/markets/indices' },
    { name: 'Stocks', href: '/markets/stocks' },
    { name: 'Cryptocurrencies', href: '/markets/crypto' },
  ],
  platforms: [
    { name: 'MetaTrader 5', href: '/platforms/mt5' },
    { name: 'Mobile Trading', href: '/platforms/mobile' },
    { name: 'Web Platform', href: '/platforms/web' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Contact Us', href: '/contact' },
    { name: 'Be a Partner', href: '/partners' },
  ],
  legal: [
    { name: 'Privacy Policy', href: 'https://tdfxcapital.com/wp-content/uploads/2025/06/TD-Capital-Ltd-Privacy-Policy-1.pdf', external: true },
    { name: 'Terms & Conditions', href: '/legal/terms' },
    { name: 'Risk Disclosure', href: 'https://tdfxcapital.com/wp-content/uploads/2025/06/TD-Capital-Ltd-Risk-Disclosure-Statement-1.pdf', external: true },
    { name: 'AML Compliance Policy', href: 'https://tdfxcapital.com/wp-content/uploads/2025/06/TD-Capital-Ltd-AML-Compliance-Policy-1.pdf', external: true },
  ],
};

export const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <img
              src="https://customer-assets.emergentagent.com/job_speedsite/artifacts/ucp499sm_TD%20Logo.png"
              alt="TD Capital"
              className="h-[58px] w-auto mb-6 brightness-0 invert"
            />
            <p className="text-gray-400 mb-6 text-sm leading-relaxed">
              Professional forex trading platform with institutional-grade technology, competitive spreads, and 24/7 support.
            </p>
            {/* Social Media Links */}
            <div className="flex flex-wrap gap-3">
              <a href="https://www.facebook.com/profile.php?id=61580191875877" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-primary rounded-lg flex items-center justify-center transition-colors" data-testid="social-facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/td_capital_/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-primary rounded-lg flex items-center justify-center transition-colors" data-testid="social-instagram">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://www.linkedin.com/company/td-capital-limited/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-primary rounded-lg flex items-center justify-center transition-colors" data-testid="social-linkedin">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://youtube.com/@tdfx-official" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-primary rounded-lg flex items-center justify-center transition-colors" data-testid="social-youtube">
                <Youtube className="h-5 w-5" />
              </a>
              <a href="http://t.me/TDFXOfficial" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 hover:bg-primary rounded-lg flex items-center justify-center transition-colors" data-testid="social-telegram">
                <Send className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="text-sm font-bold uppercase tracking-wider mb-4 text-white">
                {category}
              </h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    {link.external ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-primary transition-colors">
                        {link.name}
                      </a>
                    ) : (
                      <Link to={link.href} className="text-sm text-gray-400 hover:text-primary transition-colors">
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Address & Contact Section */}
        <div className="border-t border-gray-800 pt-12 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Registered Address */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-orange-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-2">Registered Address</h4>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Ground Floor, The Sotheby Building, Rodney Village, Rodney Bay, Gros-Iset, Saint Lucia
                </p>
              </div>
            </div>

            {/* Customer Support */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-green-500 rounded-lg flex items-center justify-center">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-2">Customer Support</h4>
                <a href="mailto:support@tdfxcapital.com" className="text-sm text-gray-400 hover:text-primary transition-colors block">
                  support@tdfxcapital.com
                </a>
                <a href="tel:+2302143127" className="text-sm text-gray-400 hover:text-primary transition-colors flex items-center gap-1 mt-1">
                  <Phone className="h-3 w-3" /> +230 214 3127
                </a>
              </div>
            </div>

            {/* Licenses & Compliance */}
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-green-500 to-orange-500 rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white mb-2">Licensing</h4>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Licensed in Saint Lucia
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Warning Section */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h4 className="text-sm font-semibold text-orange-400 mb-2 flex items-center gap-2">
              <Shield className="h-4 w-4" /> Risk Warning
            </h4>
            <p className="text-xs text-gray-400 leading-relaxed">
              An investment in derivatives may mean investors may lose an amount even greater than their original investment. Anyone wishing to invest in any of the products mentioned in TD Capital Limited or TD Capital Ltd should seek their own financial or professional advice. Trading of securities, forex, stock market, commodities, options, and futures may not be suitable for everyone and involves the risk of losing part or all of your money. Trading in the financial markets has large potential rewards, but also large potential risk. You must be aware of the risks and be willing to accept them in order to invest in the markets. Don't invest and trade with money which you can't afford to lose.
            </p>
          </div>
        </div>

        {/* Jurisdictions Notice */}
        <div className="mb-8">
          <p className="text-xs text-gray-500 leading-relaxed">
            <strong className="text-gray-400">Jurisdictions and Restrictions:</strong> TD Capital Limited does not provide services for citizens/residents of India, Russia, Iran, UAE, United States, Cuba, Iraq, Myanmar, North Korea, Sudan. The services of TD Capital Limited are not intended for distribution to, or use by, any person in any country or jurisdiction where such distribution or use would be contrary to applicable local law.
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} TD CAPITAL LTD. All rights reserved.</p>
            <p className="text-center md:text-right text-xs">
              TD Capital Limited is licensed in Saint Lucia.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;